import { useContactInvalidation } from "@/hooks/use-contact-invalidation";
import {
  createContactV1,
  deleteContactV1,
  getSearchContactsNextPageNumber,
  isPossibleDuplicateResponse,
  retrieveContactV1,
  searchContactsV3,
  searchContactIds,
  buildContactSearchRequest,
  updateContactV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type Contact } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, type QueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";
import type { ContactSearchFilters } from "@/lib/data/requests";

import { getSearchScope } from "@/lib/data/react-query/search/scope";

type ContactSearchObject = Record<string, string[] | undefined>;

export const templateContactsQueryOptions = (
  contactIds: string[],
  queryClient: QueryClient,
  canReadContactDetails = true,
) => {
  const ids = [...new Set(contactIds.filter(Boolean))].sort();
  return {
    queryKey: [
      queryKeys.contact,
      "template-contacts",
      ids,
      canReadContactDetails,
      getSearchScope(),
    ],
    enabled: ids.length > 0,
    retry: false,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!canReadContactDetails) {
        const response = await searchContactIds({ contactIds: ids }, signal);
        return response.data;
      }
      const contacts: Contact[] = [];
      // Reuse profile caches and limit detail reads for matters with many parties.
      for (let offset = 0; offset < ids.length; offset += 6) {
        signal.throwIfAborted();
        const responses = await Promise.all(
          ids
            .slice(offset, offset + 6)
            .map((id) =>
              queryClient.fetchQuery(retrieveContactQueryOptions(id)),
            ),
        );
        contacts.push(...responses.map((response) => response.data));
      }
      return contacts;
    },
  };
};

const isSearchObject = (
  filters: ContactSearchFilters | ContactSearchObject,
): filters is ContactSearchObject =>
  Object.values(filters).every(
    (value) => value === undefined || Array.isArray(value),
  );

const buildContactSearchFilters = (
  searchObject: ContactSearchObject,
): ContactSearchFilters => {
  const city = searchObject.city;
  const state = searchObject.state;
  const street1 = searchObject.street1;
  const zipCode = searchObject.zipCode;
  const emailAddress = searchObject.emailAddress;
  const faxNumber = searchObject.faxNumber;
  const phoneNumber = searchObject.phoneNumber;
  const search = searchObject.search;
  const tagLabel = searchObject.tagLabel;
  const websiteAddress = searchObject.websiteAddress;

  return {
    contactIds: searchObject.contactIds,
    excludeContactIds: searchObject.excludeContactIds,
    matterIds: searchObject.matterIds,
    individualSubTypes: searchObject.individualSubTypes,
    name: searchObject.name,
    organizationSubTypes: searchObject.organizationSubTypes,
    preferredContactMethod: searchObject.preferredContactMethod,
    ...(search?.length
      ? { search: search.length === 1 ? search[0] : search }
      : {}),
    title: searchObject.title,
    type: searchObject.type,
    ...(city?.length || state?.length || street1?.length || zipCode?.length
      ? { addresses: { city, state, street1, zipCode } }
      : {}),
    ...(emailAddress?.length ? { emails: { address: emailAddress } } : {}),
    ...(faxNumber?.length ? { faxes: { number: faxNumber } } : {}),
    ...(phoneNumber?.length ? { phones: { number: phoneNumber } } : {}),
    ...(tagLabel?.length ? { tagConnections: { label: tagLabel } } : {}),
    ...(websiteAddress?.length
      ? { websites: { address: websiteAddress } }
      : {}),
  };
};

export const retrieveContactQueryOptions = (
  contactId?: Parameters<typeof retrieveContactV1>[0],
) => {
  return {
    queryKey: [queryKeys.contact, contactId],
    enabled: !!contactId,
    queryFn: async () => retrieveContactV1(contactId),
  };
};

export const searchContactsQueryOptions = (
  search: ContactSearchFilters | ContactSearchObject,
  paginationAndSorting: Pick<
    ContactSearchFilters,
    | "context"
    | "contactIds"
    | "excludeContactIds"
    | "matterIds"
    | "order"
    | "pageNumber"
    | "pageSize"
    | "sort"
  > = {},
) => {
  const filters = getContactSearchFilters(search, paginationAndSorting);
  return {
    queryKey: [
      queryKeys.contact,
      "search",
      filters.contactIds && !filters.pageSize && !filters.pageNumber
        ? "ids"
        : "list",
      getSearchScope(),
      buildContactSearchRequest(filters),
    ],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      filters.contactIds && !filters.pageSize && !filters.pageNumber
        ? searchContactIds(filters, signal)
        : searchContactsV3(filters, signal),
  };
};

const getContactSearchFilters = (
  search: ContactSearchFilters | ContactSearchObject,
  paginationAndSorting: Pick<
    ContactSearchFilters,
    | "context"
    | "contactIds"
    | "excludeContactIds"
    | "matterIds"
    | "order"
    | "pageNumber"
    | "pageSize"
    | "sort"
  > = {},
) =>
  ({
    ...(isSearchObject(search) ? buildContactSearchFilters(search) : search),
    ...paginationAndSorting,
  }) satisfies ContactSearchFilters;

export const searchContactsInfiniteQueryOptions = (
  search: ContactSearchFilters | ContactSearchObject,
  paginationAndSorting: Pick<
    ContactSearchFilters,
    | "context"
    | "contactIds"
    | "excludeContactIds"
    | "matterIds"
    | "order"
    | "pageSize"
    | "sort"
  > = {},
) => {
  const filters = getContactSearchFilters(search, paginationAndSorting);

  return {
    queryKey: [
      queryKeys.contact,
      "search",
      "infinite",
      getSearchScope(),
      buildContactSearchRequest(filters),
    ],
    initialPageParam: 1,
    queryFn: ({
      pageParam,
      signal,
    }: {
      pageParam: number;
      signal: AbortSignal;
    }) => searchContactsV3({ ...filters, pageNumber: pageParam }, signal),
    getNextPageParam: getSearchContactsNextPageNumber,
  };
};

export const searchContactsCountQueryOptions = (
  search: ContactSearchFilters | ContactSearchObject,
) => {
  const filters = getContactSearchFilters(search);
  return {
    queryKey: [
      queryKeys.contact,
      "search",
      "count",
      getSearchScope(),
      buildContactSearchRequest(filters, "count"),
    ],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      searchContactsV3(filters, signal, "count"),
  };
};

export const useCreateContact = ({
  invalidateOnSettled = true,
  showSuccessToast = true,
}: {
  invalidateOnSettled?: boolean;
  /** Set false when the caller shows a follow-up toast (e.g. after creating a matter connection). */
  showSuccessToast?: boolean;
} = {}) => {
  const invalidateContact = useContactInvalidation();

  return useMutation({
    mutationFn: (data: Parameters<typeof createContactV1>[0]) =>
      createContactV1(data),

    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating contact");
    },

    onSettled: async (response) => {
      if (
        invalidateOnSettled &&
        response &&
        !isPossibleDuplicateResponse(response)
      ) {
        await invalidateContact();
      }
    },
    onSuccess: (response) => {
      if (showSuccessToast && !isPossibleDuplicateResponse(response)) {
        toast.success("Contact created successfully");
      }
    },
  });
};

export const useUpdateContact = ({
  invalidateOnSettled = true,
  showSuccessToast = true,
}: {
  invalidateOnSettled?: boolean;
  showSuccessToast?: boolean;
} = {}) => {
  const invalidateContact = useContactInvalidation();

  return useMutation({
    mutationFn: (data: Parameters<typeof updateContactV1>[0]) =>
      updateContactV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating contact");
    },
    onSettled: async () => {
      if (invalidateOnSettled) {
        await invalidateContact();
      }
    },
    onSuccess: () => {
      if (showSuccessToast) {
        toast.success("Contact updated successfully");
      }
    },
  });
};

export const useDeleteContact = () => {
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteContactV1>[0]) =>
      deleteContactV1(data),
    onError: () => {
      toast.error("Error deleting contact");
    },

    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      toast.success("Contact deleted successfully");
    },
  });
};
